/**
 * `dependency` (Dependent On) — `DependencyValue`
 * (`specs/M2-tier2-column-types.md` §5.19, `[09§A.7 dependency]`, `[cvs§2 dependency]`).
 *
 * Like `board_relation`, monday's `value` is **null on every row, even a populated one**; the
 * links are only in `linked_item_ids` + `dependency_links`. All 60 sampled cells agree, and on
 * the 20 populated ones every `dependency_type` and `lag` was null — plain finish-to-start with
 * no lag, which is the only variant this account uses.
 *
 * **Stored shape** is monday's documented *write* payload, `{"item_ids":["…"]}`
 * `[09§A.7 dependency]` — the same one the importer already writes (`stages/relations.ts`),
 * plus `dependency_links` when a link carries a non-null type or lag, so nothing is invented.
 *
 * `DependencyLink { dependency_type lag linked_item_id }` with `dependency_type`
 * `0 = FS, 1 = SS, 2 = FF, 3 = SF` `[09§A.7]`.
 *
 * `settings.dependency_mode` ∈ `flexible` | `strict` | `no_action` decides what happens to the
 * **successor's** dates when a predecessor moves; the one column on this account has
 * `dependency_mode: null` `[cvs§2 dependency]`, i.e. monday's own default. The auto-shift is
 * implemented by the worker (`apps/worker/src/jobs/recompute-dependencies.ts`); the modes could
 * not be measured on the sandbox (the column has no successor dates) and are implemented from
 * the documented names only — q337.
 */
import { z } from 'zod';
import { defaultSummarySql, isBlankOnly, sqlFragment } from '../sql.js';
import { textShadow, toFragmentTimestamp } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
/** `0 = finish-to-start, 1 = start-to-start, 2 = finish-to-finish, 3 = start-to-finish`. */
export const DEPENDENCY_TYPES = { FS: 0, SS: 1, FF: 2, SF: 3 };
export const dependencyLinkSchema = z.looseObject({
    linked_item_id: z.string(),
    dependency_type: z.number().int().min(0).max(3).nullable().optional(),
    /** Days. Null on every observed link. */
    lag: z.number().nullable().optional(),
});
import { dependencyFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const dependencyValueSchema = z.looseObject({
    item_ids: z.array(z.string()),
    dependency_links: z.array(dependencyLinkSchema).optional(),
    changed_at: z.string().nullable().optional(),
});
export const DEPENDENCY_MODES = ['flexible', 'strict', 'no_action'];
export const dependencySettingsSchema = z.looseObject({
    boardIds: z.array(z.union([z.number(), z.string()])).optional(),
    dependencyNewInfra: z.boolean().optional(),
    allowMultipleItems: z.boolean().optional(),
    dependency_mode: z.enum(DEPENDENCY_MODES).nullable().optional(),
    hide_footer: z.boolean().optional(),
});
/** monday's default when `dependency_mode` is null — the most conservative of the three. */
export const DEFAULT_DEPENDENCY_MODE = 'no_action';
export function dependencyMode(settings) {
    return settings.dependency_mode ?? DEFAULT_DEPENDENCY_MODE;
}
function idsFrom(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (Array.isArray(raw))
        return raw.map(String).filter((id) => /^\d+$/.test(id));
    if (typeof raw !== 'object')
        throw new ColumnValueError('dependency', 'expected an object');
    const record = raw;
    const list = record.item_ids ?? record.itemIds ?? record.linked_item_ids;
    if (Array.isArray(list))
        return list.map(String).filter((id) => /^\d+$/.test(id));
    if (Array.isArray(record.linkedPulseIds)) {
        return record.linkedPulseIds
            .map((entry) => entry !== null && typeof entry === 'object'
            ? String(entry.linkedPulseId)
            : String(entry))
            .filter((id) => /^\d+$/.test(id));
    }
    return null;
}
function normalize(raw, keepVolatile) {
    const ids = idsFrom(raw);
    if (ids === null || ids.length === 0)
        return null;
    const unique = [];
    for (const id of ids)
        if (!unique.includes(id))
            unique.push(id);
    const value = { item_ids: unique };
    if (raw !== null && typeof raw === 'object') {
        const links = raw.dependency_links;
        if (Array.isArray(links)) {
            // Only kept when a link says something the ids do not (X2-4); a list of plain
            // finish-to-start links with no lag adds nothing and is dropped.
            const meaningful = links
                .map((entry) => dependencyLinkSchema.safeParse(entry))
                .filter((parsed) => parsed.success)
                .map((parsed) => parsed.data)
                .filter((link) => (link.dependency_type ?? null) !== null || (link.lag ?? null) !== null);
            if (meaningful.length > 0)
                value.dependency_links = meaningful;
        }
        if (keepVolatile) {
            const source = raw;
            if ('changed_at' in source)
                value.changed_at = source.changed_at;
        }
    }
    return value;
}
export function dependencyDisplayValue(value, ctx) {
    if (value === null)
        return '';
    return value.item_ids
        .map((id) => ctx?.resolveItemName?.(id))
        .filter((name) => name !== undefined && name !== '')
        .join(', ');
}
/** The link for one predecessor, defaulting to plain finish-to-start with no lag. */
export function linkFor(value, itemId) {
    const explicit = value.dependency_links?.find((link) => link.linked_item_id === itemId);
    return explicit ?? { linked_item_id: itemId, dependency_type: null, lag: null };
}
export const dependencyColumn = defineColumnType({
    type: 'dependency',
    title: 'Dependency',
    description: 'The items this one depends on.',
    apiFragmentName: 'DependencyValue',
    legacyTypeName: 'dependency',
    /** monday's own id is `dependent_on` `[09§A.7 rule 2]`. */
    idPrefix: 'dependent_on',
    valueSchema: dependencyValueSchema,
    settingsSchema: dependencySettingsSchema,
    defaultSettings: { allowMultipleItems: true },
    settingsStrSchema: dependencySettingsSchema,
    writable: true,
    supportsSimpleValue: false,
    clearForms: ['null'],
    filterConditions: dependencyFilterConditions,
    filterOperators: operatorsOf(dependencyFilterConditions),
    aggregations: ['count_filled', 'count_empty', 'count', 'none'],
    /** `text: null` on every row, populated or not `[cvs§2 dependency]`. */
    emptyText: null,
    parseValue(raw) {
        return normalize(raw, false);
    },
    toText() {
        return null;
    },
    toSortKeys(value, _settings, ctx) {
        return { text: textShadow(dependencyDisplayValue(value, ctx)), number: null, date: null };
    },
    isEqual(a, b) {
        const left = a?.item_ids ?? [];
        const right = b?.item_ids ?? [];
        if (left.length !== right.length || !left.every((id, index) => id === right[index]))
            return false;
        const linkKey = (value) => JSON.stringify((value?.dependency_links ?? [])
            .map((link) => [link.linked_item_id, link.dependency_type ?? null, link.lag ?? null])
            .sort());
        return linkKey(a ?? null) === linkKey(b ?? null);
    },
    toActivityValue(value) {
        return value === null ? null : { item_ids: [...value.item_ids] };
    },
    fromMondayValue(raw) {
        return normalize(raw, true);
    },
    toMondayValue(value) {
        if (value === null)
            return null;
        const out = { item_ids: [...value.item_ids] };
        if (value.dependency_links !== undefined)
            out.dependency_links = value.dependency_links.map((l) => ({ ...l }));
        if (value.changed_at !== undefined)
            out.changed_at = value.changed_at;
        return out;
    },
    toSimpleValue() {
        return null;
    },
    fromMondaySettings(raw) {
        return dependencySettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    /**
     * **An empty cell still answers every non-nullable field of its fragment.** monday returns
     * the zero values, not an absent key: recording `0261` answers an unset `dependency` and an
     * unset `board_relation` with `{display_value: "", linked_item_ids: [], linked_items: []}`,
     * `0260` answers an unset `tags` with `{tags: []}` and `0263` answers an unset
     * `time_tracking` with `{running: false, started_at: null, duration: 0, history: []}`.
     * Returning `{}` leaves the non-nullable fields `undefined`, and **one** empty cell then
     * nulls the whole `items` response (T-5s, M9 §12.2a F-1).
     *
     * The three fragment fields that are **rows** rather than projections —
     * `linked_items`, `subitems` and `history` — are resolved by `/v2` itself
     * (`apps/api/src/graphql/v2/column-value-fields.ts`): the registry has no database.
     */
    toApiFragment(value, _settings, ctx) {
        if (value === null) {
            return { display_value: '', linked_item_ids: [], dependency_links: [], updated_at: null };
        }
        return {
            display_value: dependencyDisplayValue(value, ctx),
            linked_item_ids: [...value.item_ids],
            dependency_links: value.item_ids.map((id) => {
                const link = linkFor(value, id);
                return {
                    linked_item_id: id,
                    dependency_type: link.dependency_type ?? null,
                    lag: link.lag ?? null,
                };
            }),
            updated_at: toFragmentTimestamp(value.changed_at),
        };
    },
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    bucketsOf(value, _settings, ctx) {
        return (value?.item_ids ?? []).map((id) => ({ key: id, label: ctx?.nameOf?.(id) ?? `#${id}` }));
    },
    filterSql(ctx) {
        const cell = ctx.refs?.cell ?? 'cv';
        switch (ctx.operator) {
            case 'is_empty':
                return sqlFragment(`(${cell}.item_id IS NULL OR ${cell}.value IS NULL)`);
            case 'is_not_empty':
                return sqlFragment(`${cell}.value IS NOT NULL`);
            case 'any_of':
            case 'not_any_of': {
                if (isBlankOnly(ctx.compareValues)) {
                    return ctx.operator === 'any_of'
                        ? sqlFragment(`(${cell}.item_id IS NULL OR ${cell}.value IS NULL)`)
                        : sqlFragment(`${cell}.value IS NOT NULL`);
                }
                const ids = ctx.compareValues.map(String).filter((id) => /^\d+$/.test(id));
                if (ids.length === 0)
                    return null;
                const test = ids.map(() => `${cell}.value -> 'item_ids' @> ?::jsonb`).join(' OR ');
                const params = ids.map((id) => JSON.stringify([id]));
                return ctx.operator === 'any_of'
                    ? sqlFragment(`(${test})`, params)
                    : sqlFragment(`(${cell}.item_id IS NULL OR NOT (${test}))`, params);
            }
            default:
                return null;
        }
    },
    summarySql(ctx) {
        return defaultSummarySql(ctx);
    },
});
//# sourceMappingURL=index.js.map